import React from 'react';
import { Table, Tag, Tooltip, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { AuthorCell, EntryTableActions, formatLogged } from './entryTable';
import { DEFAULT_FAULT, SEVERITY_COLORS, faultHolder } from './issueFields';
import type {
	Program,
	ProgramIssue,
} from '../../interfaces/CamProgramming.interface';

const { Text } = Typography;

interface IssuesTableProps {
	issues: ProgramIssue[];
	loading: boolean;
	/**
	 * Whether to say where each issue came in. Pointless when the table is
	 * already scoped to one review.
	 */
	showSource?: boolean;
	/** Lets the fault column name the person rather than just the role. */
	program?: Program;
	onView: (issue: ProgramIssue) => void;
	onEdit: (issue: ProgramIssue) => void;
	onDelete: (issue: ProgramIssue) => void;
}

/** Issues as one row apiece, for reading the record rather than the prose. */
const IssuesTable: React.FC<IssuesTableProps> = ({
	issues,
	loading,
	showSource = true,
	program,
	onView,
	onEdit,
	onDelete,
}) => {
	const columns: ColumnsType<ProgramIssue> = [
		{
			title: 'Severity',
			dataIndex: 'severity',
			key: 'severity',
			width: 110,
			render: (_, issue) => (
				<Tag color={SEVERITY_COLORS[issue.severity]} bordered={false}>
					{issue.severity_display}
				</Tag>
			),
		},
		// A flat table loses which review an issue was raised in, so at least
		// say whether it came from one.
		...(showSource
			? [
					{
						title: 'Caught In',
						key: 'caught_phase',
						width: 140,
						render: (_: unknown, issue: ProgramIssue) => (
							<Text>{issue.caught_phase_display}</Text>
						),
					},
			  ]
			: []),
		{
			title: 'At Fault',
			key: 'fault',
			width: 200,
			render: (_, issue) => {
				const holder = faultHolder(program, issue);
				const name = holder?.full_name || holder?.email;
				const redirected = issue.fault !== DEFAULT_FAULT;
				return (
					<div className="cam-issue-fault">
						<Text ellipsis={{ tooltip: name || issue.fault_display }}>
							{name || issue.fault_display}
						</Text>
						{redirected && (
							<Tooltip
								title={`Charged to the ${issue.fault_display.toLowerCase()} instead of the programmer.`}
							>
								<Tag color="purple" bordered={false}>
									{issue.fault_display}
								</Tag>
							</Tooltip>
						)}
						{issue.insufficient_training && (
							<Tooltip title="Logged as a training gap, so it is not charged against them.">
								<Tag color="blue" bordered={false}>
									Training
								</Tag>
							</Tooltip>
						)}
					</div>
				);
			},
		},
		{
			title: 'Reported by',
			key: 'reported_by',
			render: (_, issue) => <AuthorCell user={issue.reported_by} />,
		},
		{
			title: 'Logged',
			dataIndex: 'created_time',
			key: 'created_time',
			width: 190,
			render: (timestamp: string) => (
				<Text>{formatLogged(timestamp)}</Text>
			),
		},
		{
			title: '',
			key: 'actions',
			width: 170,
			align: 'right',
			render: (_, issue) => (
				<EntryTableActions
					onView={() => onView(issue)}
					onEdit={() => onEdit(issue)}
					onDelete={() => onDelete(issue)}
					deleteConfirmTitle="Remove this issue?"
				/>
			),
		},
	];

	return (
		<Table
			className="cam-entry-table"
			size="small"
			rowKey="id"
			loading={loading}
			columns={columns}
			dataSource={issues}
			pagination={false}
			locale={{ emptyText: 'No issues reported' }}
		/>
	);
};

export default IssuesTable;
